/**
 * Benchmark Command Center — Content quality analytics dashboard.
 *
 * Features:
 * - KPI summary cards with aggregate metrics
 * - Radar chart showing 5-category score averages
 * - Grade distribution bar chart
 * - Period selector (7d / 30d / 90d / All)
 * - Content table with per-article benchmark scores
 * - Expandable detail rows with sub-scores and AI lexical analysis
 * - Multi-workspace selector
 * - Run/re-run benchmark on individual articles
 */

import React, { useState, useMemo } from 'react';
import { isElevatedRole } from '@/lib/roles';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useWorkspaceStore } from '@/stores/useWorkspaceStore';
import {
  fetchBenchmarkResults,
  fetchBenchmarkDetail,
  fetchBenchmarkAggregate,
  fetchTenantList,
  runBenchmark,
  type BenchmarkListItem,
  type BenchmarkAggregate,
} from '@/services/benchmarkService';
import { toast } from 'sonner';
import {
  BarChart3,
  ShieldCheck,
  Play,
  ChevronDown,
  ChevronUp,
  AlertTriangle,
  CheckCircle2,
  Loader2,
  FileText,
  Bot,
  Globe,
  Check,
  TrendingUp,
  Clock,
  Fingerprint,
  Target,
} from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Button } from '@/components/ui/button';
import {
  Radar,
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  Cell,
} from 'recharts';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const CATEGORY_LABELS: Record<string, string> = {
  factual_accuracy: 'Factual',
  originality: 'Originality',
  search_intent: 'Search Intent',
  brand_voice: 'Brand Voice',
  formatting: 'Formatting',
};

const CATEGORY_WEIGHTS: Record<string, number> = {
  factual_accuracy: 30,
  originality: 25,
  search_intent: 20,
  brand_voice: 15,
  formatting: 10,
};

// A five-step green→amber→red grade ramp. This is the sanctioned semantic
// red/green data usage, kept literal so the ramp survives a re-theme.
const GRADE_COLORS: Record<string, string> = {
  A: '#16a34a',
  B: '#22c55e',
  C: '#f59e0b',
  D: '#f97316',
  F: '#ef4444',
};

const PERIODS = [
  { value: '7d', label: '7 days' },
  { value: '30d', label: '30 days' },
  { value: '90d', label: '90 days' },
  { value: 'all', label: 'All time' },
];

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function GradeBadge({ grade, score }: { grade: string; score: number }) {
  const colors: Record<string, string> = {
    A: 'bg-emerald-100 dark:bg-emerald-900/40 text-emerald-700 dark:text-emerald-400 border-emerald-200 dark:border-emerald-800',
    B: 'bg-teal-light text-teal-dark border-teal/20',
    C: 'bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-400 border-amber-200 dark:border-amber-800',
    D: 'bg-orange-100 dark:bg-orange-900/40 text-orange-700 dark:text-orange-400 border-orange-200 dark:border-orange-800',
    F: 'bg-destructive/10 text-destructive border-destructive/20',
  };
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-xs font-semibold border ${colors[grade] || 'bg-muted text-muted-foreground border-border'}`}>
      {grade} ({score})
    </span>
  );
}

function KPICard({ icon: Icon, label, value, sub }: {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  value: string;
  sub?: string;
}) {
  return (
    <div className="bg-card rounded-lg border p-4 flex items-start gap-3">
      <div className="h-9 w-9 rounded-lg bg-coral-light flex items-center justify-center shrink-0">
        <Icon className="h-4.5 w-4.5 text-coral-dark" />
      </div>
      <div>
        <div className="text-xs text-muted-foreground">{label}</div>
        <div className="text-2xl font-semibold leading-tight">{value}</div>
        {sub && <div className="text-xs text-muted-foreground mt-0.5">{sub}</div>}
      </div>
    </div>
  );
}

function ScoreBar({ score, label }: { score: number; label: string }) {
  const color =
    score >= 80 ? 'bg-emerald-500' :
    score >= 60 ? 'bg-teal' :
    score >= 40 ? 'bg-amber-500' :
    'bg-destructive';
  return (
    <div className="flex items-center gap-2 text-sm">
      <span className="w-40 text-muted-foreground truncate">{label}</span>
      <div className="flex-1 h-2 bg-muted rounded-full overflow-hidden">
        <div className={`h-full rounded-full ${color}`} style={{ width: `${Math.min(100, score)}%` }} />
      </div>
      <span className="w-10 text-right font-mono text-xs">{score}</span>
    </div>
  );
}

function BenchmarkDetail({ contentId }: { contentId: string }) {
  const { data, isLoading } = useQuery({
    queryKey: ['benchmark', 'detail', contentId],
    queryFn: () => fetchBenchmarkDetail(contentId),
    enabled: !!contentId,
  });

  if (isLoading) {
    return <div className="p-4 text-center text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin inline mr-2" />Loading...</div>;
  }
  if (!data || data.composite_score === 0) {
    return <div className="p-4 text-center text-muted-foreground">No benchmark data available</div>;
  }

  return (
    <div className="space-y-4 p-4 bg-warm-bg rounded-lg border">
      {/* Category scores */}
      <div className="space-y-2">
        <h4 className="text-sm font-semibold text-foreground">Category Scores</h4>
        {data.categories
          .sort((a, b) => (CATEGORY_WEIGHTS[b.category] ?? 0) - (CATEGORY_WEIGHTS[a.category] ?? 0))
          .map((cat) => (
            <div key={cat.category} className="space-y-1">
              <ScoreBar
                score={cat.score}
                label={`${CATEGORY_LABELS[cat.category] || cat.category} (${CATEGORY_WEIGHTS[cat.category] || 0}%)`}
              />
              {cat.sub_scores.length > 0 && (
                <div className="ml-4 space-y-0.5">
                  {cat.sub_scores.map((sub) => (
                    <div key={sub.name} className="flex items-center gap-2 text-xs text-muted-foreground">
                      <span className="w-36 truncate">{sub.name.replace(/_/g, ' ')}</span>
                      <div className="flex-1 h-1 bg-muted rounded-full overflow-hidden">
                        <div
                          className={`h-full rounded-full ${sub.score >= 70 ? 'bg-teal/60' : sub.score >= 40 ? 'bg-amber-400/60' : 'bg-destructive/60'}`}
                          style={{ width: `${Math.min(100, sub.score)}%` }}
                        />
                      </div>
                      <span className="w-8 text-right font-mono">{sub.score}</span>
                    </div>
                  ))}
                </div>
              )}
              {cat.findings.length > 0 && (
                <div className="ml-4">
                  {cat.findings.slice(0, 3).map((f, i) => (
                    <div key={i} className="flex items-start gap-1 text-xs text-amber-600 dark:text-amber-400">
                      <AlertTriangle className="h-3 w-3 mt-0.5 shrink-0" />
                      <span>{f}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
      </div>

      {/* AI Lexical */}
      {data.ai_lexical && data.ai_lexical.total_flags > 0 && (
        <div className="space-y-2 pt-2 border-t">
          <h4 className="text-sm font-semibold flex items-center gap-1.5">
            <Bot className="h-4 w-4" />
            AI Lexical Fingerprint
          </h4>
          <div className="grid grid-cols-3 gap-2 text-xs">
            <div className="bg-card rounded p-2 border">
              <div className="text-muted-foreground">Flags</div>
              <div className="font-semibold">{data.ai_lexical.total_flags} ({data.ai_lexical.flags_per_1000_words}/1k)</div>
            </div>
            <div className="bg-card rounded p-2 border">
              <div className="text-muted-foreground">Burstiness</div>
              <div className="font-semibold">{data.ai_lexical.burstiness_score.toFixed(0)}/100</div>
            </div>
            <div className="bg-card rounded p-2 border">
              <div className="text-muted-foreground">Passive Voice</div>
              <div className="font-semibold">{(data.ai_lexical.passive_voice_ratio * 100).toFixed(1)}%</div>
            </div>
          </div>
          {Object.keys(data.ai_lexical.flagged_words).length > 0 && (
            <div className="flex flex-wrap gap-1">
              {Object.entries(data.ai_lexical.flagged_words)
                .sort(([, a], [, b]) => b - a)
                .slice(0, 10)
                .map(([word, count]) => (
                  <span key={word} className="inline-flex items-center px-1.5 py-0.5 rounded text-xs bg-destructive/10 text-destructive border border-destructive/20">
                    {word} {count > 1 && `(${count}x)`}
                  </span>
                ))}
            </div>
          )}
        </div>
      )}

      <div className="flex items-center gap-4 text-xs text-muted-foreground pt-2 border-t">
        <span>{data.word_count} words</span>
        <span>{data.duration_ms}ms</span>
        {data.llm_calls > 0 && <span>{data.llm_calls} LLM calls</span>}
        {data.model_used && <span>Model: {data.model_used}</span>}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main Page
// ---------------------------------------------------------------------------

export default function BenchmarkDashboardPage() {
  const { userProfile } = useWorkspaceStore();
  const queryClient = useQueryClient();
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [runningIds, setRunningIds] = useState<Set<string>>(new Set());
  const [selectedTenants, setSelectedTenants] = useState<string[]>([]);
  const [tenantFilterOpen, setTenantFilterOpen] = useState(false);
  const [period, setPeriod] = useState('30d');

  const platformRole = userProfile?.platform_role;
  const isElevated = isElevatedRole(platformRole);

  const { data: tenantList } = useQuery({
    queryKey: ['tenants', 'list'],
    queryFn: fetchTenantList,
    enabled: isElevated,
    staleTime: 5 * 60 * 1000,
  });

  const toggleTenant = (tenant: string) => {
    setSelectedTenants((prev) =>
      prev.includes(tenant) ? prev.filter((t) => t !== tenant) : [...prev, tenant]
    );
  };
  const selectAllTenants = () => { if (tenantList) setSelectedTenants([...tenantList]); };
  const clearTenantSelection = () => setSelectedTenants([]);

  const effectiveTenants = useMemo(
    () => selectedTenants.length > 0 ? selectedTenants : tenantList ?? [],
    [selectedTenants, tenantList],
  );
  // Stable string key for React Query (avoids array reference changes)
  const tenantKey = effectiveTenants.join(',');

  // Aggregate KPIs
  const { data: agg } = useQuery({
    queryKey: ['benchmark', 'aggregate', tenantKey, period],
    queryFn: () => fetchBenchmarkAggregate(effectiveTenants, period),
    enabled: isElevated && effectiveTenants.length > 0,
  });

  // Content list
  const { data: listData, isLoading } = useQuery({
    queryKey: ['benchmark', 'list', tenantKey],
    queryFn: () => fetchBenchmarkResults(100, 0, effectiveTenants.length > 0 ? effectiveTenants : undefined),
    enabled: isElevated && effectiveTenants.length > 0,
  });

  const runMutation = useMutation({
    mutationFn: (contentId: string) => runBenchmark(contentId, true),
    onMutate: (contentId) => { setRunningIds((prev) => new Set(prev).add(contentId)); },
    onSuccess: (result, contentId) => {
      setRunningIds((prev) => { const next = new Set(prev); next.delete(contentId); return next; });
      if (result.success) {
        toast.success(`Benchmark: ${result.result?.grade} (${result.result?.composite_score}/100)`);
        queryClient.invalidateQueries({ queryKey: ['benchmark'] });
      } else {
        toast.error(`Failed: ${result.error}`);
      }
    },
    onError: (err: Error, contentId) => {
      setRunningIds((prev) => { const next = new Set(prev); next.delete(contentId); return next; });
      toast.error(err.message);
    },
  });

  // Radar chart data
  const radarData = useMemo(() => {
    if (!agg?.category_averages) return [];
    return Object.entries(agg.category_averages).map(([key, value]) => ({
      category: CATEGORY_LABELS[key] || key,
      score: value,
      fullMark: 100,
    }));
  }, [agg]);

  // Grade distribution data
  const gradeData = useMemo(() => {
    if (!agg?.grade_distribution) return [];
    return ['A', 'B', 'C', 'D', 'F']
      .filter((g) => (agg.grade_distribution[g] ?? 0) > 0)
      .map((g) => ({
        grade: g,
        count: agg.grade_distribution[g] ?? 0,
        fill: GRADE_COLORS[g] || '#94a3b8',
      }));
  }, [agg]);

  // Gate
  if (!isElevated) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-16 text-center">
        <ShieldCheck className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
        <h2 className="text-xl font-semibold mb-2">Admin Access Required</h2>
        <p className="text-muted-foreground">Restricted to Super Admins and Platform Owners.</p>
      </div>
    );
  }

  const items = listData?.items ?? [];

  return (
    <div className="max-w-7xl mx-auto px-4 py-8 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-lg bg-coral-light flex items-center justify-center">
            <BarChart3 className="h-5 w-5 text-coral-dark" />
          </div>
          <div>
            <h1 className="text-xl font-semibold">Content Benchmarks</h1>
            <p className="text-sm text-muted-foreground">5-category quality evaluation across workspaces</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {/* Period selector */}
          <div className="flex bg-muted rounded-lg p-0.5">
            {PERIODS.map((p) => (
              <button
                key={p.value}
                onClick={() => setPeriod(p.value)}
                className={`px-3 py-1 text-xs rounded-md transition-colors ${
                  period === p.value ? 'bg-card shadow-sm font-medium' : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                {p.label}
              </button>
            ))}
          </div>

          {/* Workspace selector */}
          <Popover open={tenantFilterOpen} onOpenChange={setTenantFilterOpen}>
            <PopoverTrigger asChild>
              <Button variant="outline" className="flex items-center gap-2">
                <Globe className="h-4 w-4" />
                {selectedTenants.length === 0
                  ? 'All Workspaces'
                  : `${selectedTenants.length} workspace${selectedTenants.length > 1 ? 's' : ''}`}
                <ChevronDown className="h-3 w-3 opacity-50" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-72 p-2" align="end">
              <div className="flex items-center justify-between px-2 py-1 mb-1">
                <span className="text-xs font-medium text-muted-foreground">Workspaces</span>
                <div className="flex gap-1">
                  <button onClick={selectAllTenants} className="text-xs text-coral hover:underline">All</button>
                  <span className="text-xs text-muted-foreground">/</span>
                  <button onClick={clearTenantSelection} className="text-xs text-coral hover:underline">None</button>
                </div>
              </div>
              <div className="max-h-64 overflow-y-auto space-y-0.5">
                {(tenantList ?? []).map((tenant) => (
                  <button
                    key={tenant}
                    onClick={() => toggleTenant(tenant)}
                    className="flex items-center gap-2 w-full text-left px-2 py-1.5 rounded text-sm hover:bg-warm-bg transition-colors"
                  >
                    <div className={`h-4 w-4 rounded border flex items-center justify-center ${
                      selectedTenants.includes(tenant) ? 'bg-coral border-coral text-white' : 'border-border'
                    }`}>
                      {selectedTenants.includes(tenant) && <Check className="h-3 w-3" />}
                    </div>
                    <span className="truncate">{tenant}</span>
                  </button>
                ))}
              </div>
            </PopoverContent>
          </Popover>

          <button
            onClick={() => {
              const unbenchmarked = items.filter((i) => i.grade === '—').slice(0, 5);
              unbenchmarked.forEach((item) => runMutation.mutate(item.content_id));
              if (unbenchmarked.length > 0) toast.info(`Running ${unbenchmarked.length} benchmarks...`);
              else toast.info('All articles already benchmarked');
            }}
            disabled={runMutation.isPending}
            className="flex items-center gap-2 px-4 py-2 bg-coral text-white rounded-lg hover:bg-coral-hover transition-colors disabled:opacity-50"
          >
            <Play className="h-4 w-4" />
            Run Pending
          </button>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-4 gap-4">
        <KPICard
          icon={Target}
          label="Avg Quality Score"
          value={agg?.avg_composite ? `${agg.avg_composite}` : '—'}
          sub={agg?.benchmarked_articles ? `${agg.benchmarked_articles} of ${agg.total_articles} benchmarked` : undefined}
        />
        <KPICard
          icon={FileText}
          label="Total Articles"
          value={`${agg?.total_articles ?? 0}`}
          sub={effectiveTenants.length > 1 ? `across ${effectiveTenants.length} workspaces` : undefined}
        />
        <KPICard
          icon={Clock}
          label="Avg Generation Time"
          value={agg?.avg_generation_time ? `${agg.avg_generation_time}s` : '—'}
        />
        <KPICard
          icon={Fingerprint}
          label="AI Flags / 1k Words"
          value={agg?.avg_ai_flags ? `${agg.avg_ai_flags}` : '—'}
          sub={agg?.avg_ai_flags !== undefined ? (agg.avg_ai_flags < 3 ? 'Low detection' : agg.avg_ai_flags < 8 ? 'Moderate' : 'High detection') : undefined}
        />
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-2 gap-4">
        {/* Radar Chart */}
        <div className="bg-card rounded-lg border p-4">
          <h3 className="text-sm font-semibold mb-3">Category Averages</h3>
          {radarData.length > 0 ? (
            <ResponsiveContainer width="100%" height={280}>
              <RadarChart cx="50%" cy="50%" outerRadius="70%" data={radarData}>
                <PolarGrid stroke="hsl(var(--border))" />
                <PolarAngleAxis
                  dataKey="category"
                  tick={{ fontSize: 11, fill: 'hsl(var(--muted-foreground))' }}
                />
                <PolarRadiusAxis
                  angle={90}
                  domain={[0, 100]}
                  tick={{ fontSize: 10, fill: 'hsl(var(--muted-foreground))' }}
                  tickCount={5}
                />
                <Radar
                  dataKey="score"
                  stroke="var(--color-coral)"
                  fill="var(--color-coral)"
                  fillOpacity={0.2}
                  strokeWidth={2}
                />
              </RadarChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-[280px] flex items-center justify-center text-sm text-muted-foreground">
              Run benchmarks to see category scores
            </div>
          )}
        </div>

        {/* Grade Distribution */}
        <div className="bg-card rounded-lg border p-4">
          <h3 className="text-sm font-semibold mb-3">Grade Distribution</h3>
          {gradeData.length > 0 ? (
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={gradeData} layout="vertical" margin={{ left: 10, right: 20 }}>
                <XAxis type="number" tick={{ fontSize: 11 }} allowDecimals={false} />
                <YAxis type="category" dataKey="grade" tick={{ fontSize: 14, fontWeight: 600 }} width={30} />
                <Tooltip
                  formatter={(value: number) => [`${value} articles`, 'Count']}
                  contentStyle={{ borderRadius: 8, fontSize: 12 }}
                />
                <Bar dataKey="count" radius={[0, 6, 6, 0]} barSize={28}>
                  {gradeData.map((entry) => (
                    <Cell key={entry.grade} fill={entry.fill} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-[280px] flex items-center justify-center text-sm text-muted-foreground">
              No benchmark data yet
            </div>
          )}
        </div>
      </div>

      {/* Content Table */}
      <div className="bg-card rounded-lg border overflow-hidden">
        <div className="px-4 py-3 border-b bg-muted/50 flex items-center justify-between">
          <h3 className="text-sm font-semibold">Article Benchmark Scores</h3>
          <span className="text-xs text-muted-foreground">{items.length} articles</span>
        </div>
        <table className="w-full">
          <thead>
            <tr className="border-b">
              <th className="text-left text-xs font-medium text-muted-foreground px-4 py-2.5">Keyword</th>
              <th className="text-left text-xs font-medium text-muted-foreground px-4 py-2.5">Workspace</th>
              <th className="text-center text-xs font-medium text-muted-foreground px-4 py-2.5">Grade</th>
              <th className="text-center text-xs font-medium text-muted-foreground px-4 py-2.5">Score</th>
              <th className="text-center text-xs font-medium text-muted-foreground px-4 py-2.5">Words</th>
              <th className="text-center text-xs font-medium text-muted-foreground px-4 py-2.5">AI Flags</th>
              <th className="text-center text-xs font-medium text-muted-foreground px-4 py-2.5">Model</th>
              <th className="text-center text-xs font-medium text-muted-foreground px-4 py-2.5 w-20">Actions</th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr><td colSpan={8} className="text-center py-8 text-muted-foreground"><Loader2 className="h-5 w-5 animate-spin inline mr-2" />Loading...</td></tr>
            ) : items.length === 0 ? (
              <tr><td colSpan={8} className="text-center py-8 text-muted-foreground"><FileText className="h-8 w-8 mx-auto mb-2 opacity-40" />No completed content found</td></tr>
            ) : (
              items.map((item) => (
                <React.Fragment key={item.content_id}>
                  <tr
                    className={`border-b hover:bg-muted/50 cursor-pointer transition-colors ${expandedId === item.content_id ? 'bg-warm-bg' : ''}`}
                    onClick={() => setExpandedId(expandedId === item.content_id ? null : item.content_id)}
                  >
                    <td className="px-4 py-2.5">
                      <div className="flex items-center gap-2">
                        {expandedId === item.content_id
                          ? <ChevronUp className="h-4 w-4 text-muted-foreground shrink-0" />
                          : <ChevronDown className="h-4 w-4 text-muted-foreground shrink-0" />}
                        <span className="text-sm font-medium truncate max-w-[220px]">{item.keyword || 'Untitled'}</span>
                      </div>
                    </td>
                    <td className="px-4 py-2.5"><span className="text-xs text-muted-foreground truncate max-w-[130px] block">{item.tenant || '—'}</span></td>
                    <td className="px-4 py-2.5 text-center">
                      {item.grade !== '—' ? <GradeBadge grade={item.grade} score={item.composite_score} /> : <span className="text-xs text-muted-foreground">—</span>}
                    </td>
                    <td className="px-4 py-2.5 text-center text-sm font-mono">{item.composite_score > 0 ? item.composite_score : '—'}</td>
                    <td className="px-4 py-2.5 text-center text-sm text-muted-foreground">{item.word_count || '—'}</td>
                    <td className="px-4 py-2.5 text-center">
                      {item.ai_flags > 0 ? (
                        <span className={`text-sm font-mono ${item.ai_flags > 5 ? 'text-destructive' : item.ai_flags > 2 ? 'text-amber-600 dark:text-amber-400' : 'text-muted-foreground'}`}>{item.ai_flags}</span>
                      ) : item.grade !== '—' ? (
                        <CheckCircle2 className="h-4 w-4 text-emerald-500 dark:text-emerald-400 mx-auto" />
                      ) : <span className="text-xs text-muted-foreground">—</span>}
                    </td>
                    <td className="px-4 py-2.5 text-center text-xs text-muted-foreground truncate max-w-[100px]">{item.model_used ? item.model_used.split('/').pop() : '—'}</td>
                    <td className="px-4 py-2.5 text-center">
                      <button
                        onClick={(e) => { e.stopPropagation(); runMutation.mutate(item.content_id); }}
                        disabled={runningIds.has(item.content_id)}
                        className="inline-flex items-center gap-1 px-2 py-1 text-xs rounded border hover:bg-muted disabled:opacity-50 transition-colors"
                      >
                        {runningIds.has(item.content_id) ? <Loader2 className="h-3 w-3 animate-spin" /> : <Play className="h-3 w-3" />}
                        {item.grade !== '—' ? 'Re-run' : 'Run'}
                      </button>
                    </td>
                  </tr>
                  {expandedId === item.content_id && (
                    <tr>
                      <td colSpan={8} className="px-4 py-2">
                        <BenchmarkDetail contentId={item.content_id} />
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
